import { Box, Card, styled } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useCallback, useMemo, useState } from 'react';
import { LuTrash2 } from 'react-icons/lu';
import {
  IAutomatedCampaignCandidatEmailList,
  triggerAiAutomatedCampaignFinalizedCandidates,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  WsButton,
  WsColors,
  WsDataGrid,
  WsDrawer,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard';
import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import { useNotificationStore } from '../../../../utils/NotificationStore';

interface ConfirmEmailCampaignDrawerProps {
  open: boolean;
  onClose: () => void;
  campaignEmailCandidateData: IAutomatedCampaignCandidatEmailList;
}
const BottomBar = styled(Box)(() => ({
  position: 'sticky',
  bottom: 0,
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  backgroundColor: WsColors.TertiaryShades100,
}));

const CampaignEmailDrawer: React.FC<ConfirmEmailCampaignDrawerProps> = ({
  open,
  onClose,
  campaignEmailCandidateData,
}) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const handlePage = (newPage: number) => setPage(newPage);

  const handlePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    handlePage(0);
  };
  const [rows, setRows] = useState(() =>
    (campaignEmailCandidateData?.candidateEmails ?? [])?.map((email, i) => ({
      id: i + 1,
      candidateName: `Candidate ${i + 1}`,
      email: email,
      isQueued: true,
    }))
  );

  const handleUnqueue = useCallback((id: number) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, isQueued: false } : row))
    );
  }, []);

  const confirmColumns: GridColDef[] = useMemo(
    () => [
      //   {
      //     field: 'candidateName',
      //     headerName: 'Candidates',
      //     sortable: false,
      //     flex: 1,
      //     renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
      //   },
      {
        field: 'email',
        headerName: 'Email',
        sortable: false,
        flex: 1,
        renderCell: (params) => <WsTypography bold>{params.value}</WsTypography>,
      },
      {
        field: 'isQueued',
        headerName: 'Remove From Queue',
        sortable: false,
        flex: 1,
        renderCell: (params) =>
          params.value ? (
            <LuTrash2 style={{ cursor: 'pointer' }} onClick={() => handleUnqueue(params.row.id)} />
          ) : (
            <WsTypography bold>Removed</WsTypography>
          ),
      },
    ],
    [handleUnqueue]
  );

  const handleSave = async () => {
    try {
      await triggerAiAutomatedCampaignFinalizedCandidates({
        ...campaignEmailCandidateData,
        candidateEmails: rows.filter((row) => row.isQueued).map((row) => row.email),
      });
      setNotification({
        message: 'Triggered AI email campaign successfully',
        type: 'success',
      });
      onClose();
    } catch (e: any) {
      console.error(e);
      if (e?.response?.status === 400) {
        setNotification({
          message: `${e?.response?.data}`,
          type: 'error',
        });
      } else {
        setNotification({
          message: 'Something went wrong!',
          type: 'error',
        });
      }
    }
  };

  return (
    <WsDrawer open={open} onClose={onClose} title="Confirm Email Campaign">
      <>
        <CardWrapper sx={{ height: '87vh', overflow: 'auto' }}>
          <Card sx={{ padding: 1 }}>
            <WsTypography bold variant={WsTypoVariants.TITLE}>
              Candidates in Email Queue
            </WsTypography>
            {rows.length === 0 ? (
              <NoDataFoundCard text="No results found" icon={<EmptyTeamsAndUsersIcon />} />
            ) : (
              <WsDataGrid
                autoHeight
                rows={rows}
                columns={confirmColumns}
                paginationMode="client"
                page={page}
                onPageChange={handlePage}
                pageSize={pageSize}
                onPageSizeChange={handlePageSize}
                totalRows={rows.length}
              />
            )}
          </Card>
        </CardWrapper>

        <BottomBar p={2}>
          <WsButton onClick={handleSave}>Confirm & Send</WsButton>
        </BottomBar>
      </>
    </WsDrawer>
  );
};

export default CampaignEmailDrawer;
